import { useEffect, useState } from 'react';
import { getSiteSettings } from '@/lib/siteSettings';


/* Convert hex (#RRGGBB) to HSL string "H S% L%" for CSS variables */
function hexToHSL(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/* Derive accent-foreground: white for dark accents, dark for light accents */
function getAccentForeground(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '20 20% 11%' : '40 10% 96%';
}

const FONT_URLS = {
  'Cormorant Garamond': 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap',
  'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;500;600;700&display=swap',
  'Merriweather': 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700&display=swap',
  'Lora': 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap',
  'Crimson Text': 'https://fonts.googleapis.com/css2?family=Crimson+Text:wght@400;600;700&display=swap',
  'EB Garamond': 'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700&display=swap',
  'Manrope': 'https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&display=swap',
  'Inter': 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
  'Poppins': 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
};

const ThemeProvider = ({ children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const applyTheme = async () => {
      try {
        const { data } = await getSiteSettings();
        const root = document.documentElement;

        // Apply accent/primary color
        if (data.primary_color) {
          const accentHSL = hexToHSL(data.primary_color);
          root.style.setProperty('--accent', accentHSL);
          root.style.setProperty('--ring', accentHSL);
          root.style.setProperty('--accent-foreground', getAccentForeground(data.primary_color));
        }

        // Apply background color
        if (data.background_color) {
          root.style.setProperty('--background', hexToHSL(data.background_color));
        }

        // Apply text/foreground color
        if (data.text_color) {
          const fgHSL = hexToHSL(data.text_color);
          root.style.setProperty('--foreground', fgHSL);
          root.style.setProperty('--card-foreground', fgHSL);
          root.style.setProperty('--popover-foreground', fgHSL);
        }

        // Apply heading font
        if (data.font_family && data.font_family !== 'Cormorant Garamond') {
          const fontUrl = FONT_URLS[data.font_family];
          if (fontUrl) {
            // Load font if not already present
            const existing = document.querySelector(`link[href="${fontUrl}"]`);
            if (!existing) {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = fontUrl;
              document.head.appendChild(link);
            }
          }
          // Override the serif font utility
          const style = document.getElementById('theme-font-override') || document.createElement('style');
          style.id = 'theme-font-override';
          style.textContent = `.font-serif { font-family: '${data.font_family}', serif !important; }`;
          if (!style.parentNode) document.head.appendChild(style);
        }

        // Apply custom CSS
        if (data.custom_css) {
          const style = document.getElementById('theme-custom-css') || document.createElement('style');
          style.id = 'theme-custom-css';
          style.textContent = data.custom_css;
          if (!style.parentNode) document.head.appendChild(style);
        }
      } catch (e) {
        console.error('Failed to load theme settings:', e);
      } finally {
        setLoaded(true);
      }
    };

    applyTheme();
  }, []);

  // Render children even before theme loads to avoid flash of nothing
  return children;
};

export default ThemeProvider;
